/**
 * 数据层类型定义。
 * 与参考站逆向出的接口契约对齐（见 逆向产物/api-contract.json），
 * 你的 Python 后端只要满足这些形状，前端零改动即可切换。
 */

export type JobState = 'queued' | 'running' | 'succeeded' | 'failed' | 'partial'
export type OutputKind = 'image' | 'video' | 'audio' | 'text'

export type JobOutput = {
  url: string
  type: OutputKind
  thumbUrl?: string
  text?: string
  model?: string
  taskId?: string
}

export type JobError = { code: string; message: string }

export type GenerateRequest = {
  /** 应用标识，对应后端 /api/aigc/ai-app/{appSlug}/run */
  appSlug: string
  /** 上游模型 slug */
  model?: string
  /** 业务输入，会被后端拼成最终 prompt */
  inputs: Record<string, unknown>
  /** 参考图公网地址 */
  imageUrls?: string[]
  /**
   * **参考视频**公网地址。
   *
   * 与 `imageUrls` 并列，不是二选一：参考图控形象、参考视频控运镜与节奏，
   * 上游 14 个视频模型里有 10 个两者都收。
   *
   * ⚠️ 别把视频拼进 `imageUrls`。后端会按「图片」处理（换成上游的
   * `input_images`），上游拿到的是一条挂在图片位上的视频素材，行为未定义。
   * 这个字段一路走到适配器的 `input_videos`。
   */
  videoUrls?: string[]
  /** 拼接后的完整提示词（前端展示 + 后端可复用） */
  prompt: string
  /**
   * 逐张提示词：一次要 N 张时，第 i 张用 prompts[i]（缺位回退到 prompt）。
   *
   * 套图类任务必须拆开传。只发一份「1. 白底正视 2. 侧面 3. 场景 …」的话，
   * 模型会把 5 种结构画进同一张画面 —— N 张结果全是同样的多视角拼贴，
   * 而不是 N 张单图。拆分语义只有前端知道，后端只按序号分发。
   */
  prompts?: string[]
  /**
   * 逐张尺寸：第 i 张用什么尺寸，可写比例（`1:1`）或**成品像素**（`970x600`）。
   *
   * 为什么必须逐张：Amazon A+ 的模块尺寸不是一个比例 —— 全宽位 970×600、
   * 横幅 970×300、三图格 300×300、单图侧栏 300×400。整批共用一个比例时，
   * 任务照记 succeeded，但铺进 A+ 后台一半模块对不上版（拉伸或留白）。
   *
   * 上游 `size` 参数比例与像素都吃（skills/ss-art-image-gen）。实测（2026-09-29）
   * 传 `970x600` 会**严格按该宽高比出图**（拿回 1595×986，比例 1.6176，
   * 与 970:600 的 1.6167 差 0.06%），像素随分辨率档位等比放大。
   * 也就是说这里写官方成品尺寸，得到的是**官方比例 + 更高像素**，
   * 铺版时等比缩小即可，不需要本地裁切 —— 本地裁切会切掉产品主体，
   * 也违反「交付图必须由生图工具直接生成」。
   */
  ratios?: string[]
  aspectRatio?: string
  resolution?: string
  /** 预估出图数量，用于 mock 与前端占位 */
  outputs?: number
  /** 预估耗时（ms），进度公式依赖它 */
  estimatedMs?: number
  /** 本次调用是否免费（部分工具免费） */
  free?: boolean
  /**
   * 任务来源页面（如 detail-page / ai-set）。后端落库用于分页面统计用量与复盘。
   * 曾经只存在于 inputs 里，导致后端 source_page 一直退化成 appSlug。
   */
  sourcePage?: string
  /** 任务展示名。缺省时后端退化成「模型名 · 提示词前 24 字」 */
  title?: string
  /**
   * 投放平台（平台档案 id，如 amazon-us / ozon / xhs）。
   *
   * 之所以要单列一个字段而不是只塞进 inputs：后端要靠它落库复盘
   * 「这张图为什么出的是中文」。inputs 是自由格式，字段名随时会被业务改掉，
   * 关键的可追溯字段必须是一等公民。
   */
  platform?: string
  /**
   * **skill 加载器开关**，默认不传（= 关）。
   *
   * 打开后后端会走 `llm.complete_with_skills()`：模型可以先用 `load_skill`
   * 读 `~/.workbuddy/skills/` 下对应技能的 `SKILL.md` 再动笔。代价是更慢、token 高一个量级，
   * 且同一次输入两次跑法可能不同（产物不可复现）。
   *
   * ⚠️ 这只是第二道开关。第一道是后端环境变量 `HW_SKILLS_ENABLE`（默认 0）；
   * 后端没开时传 true 会被**明确 400 挡掉**（SKILLS_DISABLED），不是静默忽略。
   * 所以前端只在 `GET /api/aigc/skills` 报 `enabled:true` 时才渲染那个开关 ——
   * 不放一个点了必然报错的死控件。
   */
  skills?: boolean
  /**
   * **批次 id**：详情页「批量表格」一次提交会产生 **N 个请求**（一行产品一个），
   * 它们共用同一个批次 id。
   *
   * 为什么是 N 个请求而不是一个请求装 N 个产品：`outputs` 那一维是**逐张**
   * （第 i 张用 `prompts[i]`），与「逐产品」正交。合并请求会让后端同时理解
   * 两个维度的拆解，还会让「一行失败」连坐整批。拆成 N 个 job 之后，
   * 失败边界天然落在行上，重试也天然是按行重试。
   *
   * ⚠️ 后端 `GenerateRequest` **不是 CamelModel**，多词字段全靠显式 alias。
   * client.ts 里必须显式列出这个字段（只塞进 inputs 不会被提升）。
   */
  batchId?: string
}

export type RunResponse = {
  job: { jobId: string }
  state: JobState
  outputs?: JobOutput[]
  usage?: { consumeCoins: number }
  error?: JobError
  /**
   * 提交并预扣之后的**剩余额度**（服务端权威值）。
   *
   * ⚠️ 前端**必须直接采信这个数**，不要自己拿旧余额去减：
   * 结算还会多退少补（实际出图张数可能与报价不同），自己减出来的数迟早对不上，
   * 而「界面显示的余额」与「真实余额」不一致是最难解释的一类问题。
   */
  coinBalance?: number
  /** 本次预扣的额度（= 报价折算）。用于「本单预扣 N 额度」的提示。 */
  reservedCoins?: number
}

export type PollResponse = {
  state: JobState
  outputs: JobOutput[]
  usage?: { consumeCoins: number }
  error?: JobError
}

export type PricePreviewRequest = Pick<
  GenerateRequest,
  'appSlug' | 'model' | 'outputs' | 'resolution' | 'aspectRatio'
> & { inputs?: Record<string, unknown> }

export type PricePreviewResponse = {
  ok: boolean
  estimatedPrice: number
  currency: 'CNY'
  isFreeThisCall: boolean
  /** 计价口径说明，直接显示给用户，避免「为什么是这个价」 */
  basis?: string
}

/**
 * 提示词改写（`POST /api/aigc/prompt/refine`）。
 *
 * **它不是一次生成任务**：不建 job、不进任务中心，几秒到几十秒内同步返回一段文本。
 * 但它照样在 `usage_records` 里记一条（按条 ¥0.10）—— 花钱的事必须上账本，
 * 只是不占任务位。接口形状与生成任务刻意不同，就是为了让调用方一眼看出
 * 「这是改输入，不是出产物」。
 */
export type RefinePromptRequest = {
  /** 当前这一版提示词。可空 —— 空则由 `brief` 从零写 */
  prompt: string
  /** 这支片子的上下文与改写要求。**由前端组装**：提示词模板的真源在 `config/prompts.ts` */
  brief: string
  /**
   * 改写场景。合法值 = 后端 `aigc._REFINE_SYSTEM` 的键（**那里是唯一真源**）。
   *
   * · `storyboard`    —— 改写**分镜图**提示词（3×3 九宫格，给生图模型）
   * · `template-shot` —— 扩写**全片约束**（「存为模板」弹窗里那一块）
   * · `video-prompt`  —— 汇总**成片提示词**（给视频模型；产物仅供查看与复制，不参与提交）
   * · `image-shot`    —— 改写**成品图的逐张提示词**（主图 / 平台详情模块 / 模仿版式；
   *   提交前的报价确认弹窗里，每一张旁边那颗「AI 重写」）
   */
  kind?: 'storyboard' | 'template-shot' | 'video-prompt' | 'image-shot'
  /** 出处，只用于用量账本 */
  sourcePage?: string
}

export type RefinePromptResponse = {
  prompt: string
  model: string
  /** 本次费用（元）。后端定价 + 记账，前端只负责显示 */
  cost: number
  /**
   * 本次**从额度里扣掉的积分**（1 元 = 10 积分）。**由后端给，前端不许自己乘**。
   *
   * 顶栏余额是积分口径，提示语也必须用这个数 —— 提示说「¥0.10」、余额掉「1 积分」，
   * 用户会以为扣错了。而自己 `cost * 10` 等于把换算率抄成第二份真源。
   */
  coins: number
  tokens: number
  /** 模型端耗时（毫秒） */
  elapsedMs: number
}

/**
 * 表单填充（`POST /api/aigc/form/fill`）—— `/banner` 的「AI 填充」按钮。
 *
 * 与 `RefinePromptRequest` 的差别只有一处：那个是「把一份**已有**的提示词改写一版」，
 * 这个是「用户手上只有产品名，其余字段从零补齐」。所以回来的不是一段正文，
 * 而是**可以直接写进表单的结构化字段**（键名与表单字段逐字同名）。
 *
 * 规矩与改写完全一致：不建 job、同步返回、真花钱（¥0.10 落 usage）。
 */
export type FillFormRequest = {
  /** 场景上下文。**由前端组装** —— 字段清单与提示词模板的真源在 `config/prompts.ts` */
  brief: string
  /** 填充场景（决定后端按哪套 system 问、返回哪几个字段）。见 `FillFormKind` */
  kind?: FillFormKind
  /** 出处，只用于用量账本 */
  sourcePage?: string
}

/**
 * 参考视频分析（`POST /api/aigc/video/analyze`）—— 视频复刻第 1 页的「分析视频」按钮。
 *
 * 与 `RefinePromptRequest` / `FillFormRequest` 同族（不建 job、同步返回、真花钱上账本），
 * 但**上游不是文本模型**：它把整条视频交给 apilio 的多模态模型（`qwen-vl-max`）
 * 去读，一次约 9.5K tokens（其中视频本身占 8.9K）。所以单价是**另一档**
 * （¥0.30/次，见后端 `pricing.py` 里 `qwen-vl-max` 那条），不是文案的 ¥0.10。
 *
 * ⚠️ `videoUrl` 必须是**公网绝对地址**：上游是从公网把这条片子拉过去读的，
 * 本站相对路径（`/static/...`）它取不到。传错的表现不是报错，而是
 * **一次本该 12 秒的调用干等满超时** —— 极难查。用 `lib/mediaUrl.ts` 的
 * `toAbsUrl` 转一道。
 */
export type AnalyzeVideoRequest = {
  /** 公网可访问的视频直链 */
  videoUrl: string
  /** 出处，只用于用量账本 */
  sourcePage?: string
  /** 可选上下文（如「这条片子要换成的是一个保温杯」），帮模型抓重点 */
  note?: string
}

export type AnalyzeVideoResponse = {
  /**
   * 是否解析出了镜头清单。
   *
   * 🔴 **False 时必须展示 `raw` 原文，不许渲染空清单。** 上游对视频有「假成功」的
   * 路子（回 200、正文却说「您似乎还没有上传视频」—— 内容块被静默丢了），
   * 界面上显示「分析成功 + 空列表」等于骗用户。
   */
  parsed: boolean
  /** 镜头数。**以实际解析出的条数为准**（模型自述的总数偶尔与正文条数不符） */
  shotCount: number
  /** 整体节奏一句话（如「慢，平均每个镜头 1 秒」） */
  rhythm: string
  /** 逐镜头的画面描述，一条一行 —— 渲染成可编辑的清单 */
  shots: string[]
  /** 画面风格一句话 */
  style: string
  /** 模型原文。解析失败时这是**唯一**能看的东西；解析成功时留作对照 */
  raw: string
  model: string
  /** 本次费用（元）。后端定价 + 记账，前端只负责显示 */
  cost: number
  /** 本次**从额度里扣掉的积分**（1 元 = 10 积分）。由后端给，前端不许自己乘 */
  coins: number
  tokens: number
  /** 模型端耗时（毫秒） */
  elapsedMs: number
}

/**
 * 落到**草稿**里的镜头结构 —— `AnalyzeVideoResponse` 里与提示词有关的那几项。
 *
 * 为什么不直接把整个响应存进草稿：`cost` / `coins` / `elapsedMs` / `raw` 是
 * **那一次调用**的元信息。落进草稿后它们会被当成「当前状态」渲染 —— 用户换了
 * 一条片子，页面上还写着上次花了三毛、耗时 12 秒，看着像刚刚发生过。
 * **元信息只在当次提示里用**（toast），草稿只留内容。
 */
export type VideoAnalysis = {
  /** 分析时用的那条片子 —— 换片之后这份结构就过期了（判据见 `liveVideoAnalysis`） */
  videoUrl: string
  /** 镜头数。以清单实际条数为准（用户删掉一条，它跟着减） */
  shotCount: number
  /** 整体节奏一句话 */
  rhythm: string
  /** 逐镜头画面描述，一条一行 —— 可编辑 */
  shots: string[]
  /** 画面风格一句话 */
  style: string
}

/**
 * 取**仍然对得上当前参考视频**的那份分析，对不上就返回 `null`。
 *
 * 「过期」只有一条判据：**分析时用的片子是不是现在这一条**（比对 `videoUrl`）。
 * 之所以要收成一个函数，是因为它有**两个消费者、而它们的失败方式完全不同**：
 * ```
 * VideoAnalysisCard   过期的显示成「换片了，请重新分析」+ 原始文本兜底
 * buildVideoPrompt   过期的**一个字都不许进提示词**
 * ```
 * 后者才是真正会花钱的那条路。两处各写一遍 `analysis.videoUrl !== refVideo[0]`
 * 的话，只要有一处写反（`===` 与 `!==` 恰好是最容易写反的一对），就会出现
 * **「用户换了参考视频、没重新分析，成片却按上一条片子的结构拍」** —— 而界面上
 * 卡片多半是好的，只有成片不对，对着截图根本看不出来。
 *
 * ⚠️ 判据刻意**不取 `shotCount` 或时间戳**：用户是可以逐条编辑镜头描述的，
 * 编辑之后内容变了但它**仍然属于这条片子**，不该算过期；同理也不能拿
 * 「分析发生在什么时候」判 —— 换回原来的片子时那份分析其实还有效。
 */
export function liveVideoAnalysis(
  analysis: VideoAnalysis | null | undefined,
  videoUrl: string,
): VideoAnalysis | null {
  if (!analysis) return null
  return analysis.videoUrl === videoUrl ? analysis : null
}

/**
 * 填充场景。**每个值在后端 `_FILL_SYSTEM` / `_FILL_FIELDS` 里各有一条**，
 * 两处（这里 + 后端）漂移的后果是 `FILL_KIND_UNSUPPORTED` —— 用户点了按钮
 * 直接报错，而这属于「改代码时漏同步」而不是「环境问题」。
 *
 * ⚠️ 每加一个值，**必须同时**：
 *   ① 后端 `app/routers/aigc.py` 的 `_FILL_SYSTEM` 与 `_FILL_FIELDS` 各加一条；
 *   ② 前端 `config/prompts.ts` 加对应的 `build*FillBrief`；
 *   ③ 页面把 `AiFillButton` 挂上，`fields` 要**逐字等于**该页表单的键。
 *   漏 ② 页面就没上下文可传；漏 ③ 等于加了个没人用的场景。
 */
export type FillFormKind =
  | 'banner'
  | 'video'
  | 'social'
  | 'copy'
  | 'detail'
  | 'human'
  | 'feature'
  | 'board-shots'
  /** 商品库「新增 / 编辑商品」——标题与卖点（见 `FILL_FORM_FIELDS.product`） */
  | 'product'

/**
 * 每个填充场景允许回填的字段。**前端唯一一份**：各页 `AiFillButton` 的 `fields`
 * 与 `api/mock.ts` 的返回值都从这里取，不许各写一遍。
 *
 * ⚠️ **这串键名必须与后端 `app/routers/aigc.py` 的 `_FILL_FIELDS` 逐字一致** ——
 * 跨语言没法共用一份常量，只能靠这条注释钉住。漂移的后果是「模型按 A 写、
 * 前端按 B 筛」，用户点了按钮什么都没变却报了成功。后端测试
 * `test_fill_form.py` 有一条断言专门数两边的键是否相等，改这里时记得一起改。
 *
 * 各页的字段名**逐字等于该页表单的键**（`FormState` / `BannerInput` 里那个），
 * 所以拿到就能 `patch()`，不需要任何映射层。
 */
export const FILL_FORM_FIELDS = {
  /** `/banner` 第 1 页签「文案与画面」—— 图上要出现的四段文字 */
  banner: ['mainCopy', 'subCopy', 'sellingPoints', 'extra'],
  /**
   * 视频页「内容与场景」。**只有一个键**：那一格本来就是「卖点与补充要求」
   * 合并的两个自由文本框（分开写用户不知道该进哪个），所以 AI 也只填这一格。
   * 产品名是给 AI 的**输入**，不是它的产出，不在白名单里。
   */
  video: ['sellingPoints'],
  /**
   * `/social-image` 第 1 页签 —— 卖点 / 人群 / 场景倾向。
   *
   * ⚠️ **不含 `extra`**：这一页从来没有「补充要求」的输入框（`form.extra` 在表单里
   * 没有入口），AI 填了用户既看不见也改不掉 —— 而 `buildSocialPrompts` 会把它
   * 原样拼进提示词。填一个界面上不存在的字段，等于偷偷改了要发出去的东西。
   */
  social: ['sellingPoints', 'audience', 'scene'],
  /** 文案页 —— 卖点 / 人群 / 补充（渠道与写法属于用户的投放决策，不归 AI） */
  copy: ['sellingPoints', 'audience', 'extra'],
  /** 详情页 —— 卖点（图片分节结构由平台档案决定，不归 AI） */
  detail: ['sellingPoints'],
  /**
   * `/digital-human` 第 3 步 —— 口播稿。
   *
   * 这是全站**最长**的一格（8 行 textarea），也是用户最不愿手写的一格 ——
   * 所以它填的是**完整稿子**，不是「素材」。其余页都是"补料"，
   * 只有这一页 AI 的产出与用户最终拿到的东西是同一个（见 `FILL_FORM_FIELDS` 的用法）。
   */
  human: ['script'],
  /**
   * 专业工作流档（`/professional/fashion` · `/professional/products`，共 18 档）——
   * 就是 `catalog` 里 `feature.textLabel` 那个自由文本框（「补充要求」/「卖点内容」/
   * 「文字补充要求」，逐档不同）。
   *
   * ⚠️ **只有一个键，且刻意的**：这一页另外两类输入是**选择器**
   * （模特设定 `look` / 场景设定 `scene`），它们的真源是 `config/modelProfile.ts` 与
   * `config/sceneProfile.ts`。让 AI 回填选择器 = 让模型吐枚举 id，而枚举一旦由模型生成，
   * 前端就失去了「能选什么」的权威 —— 它编一个 id 出来，界面上是个选不中的空值，
   * 提示词里却照写（和「同一份清单不许在两处各写一遍」是同一条铁律的另一面）。
   * 所以：**AI 只碰自由文本，选择器永远归用户。**
   */
  feature: ['requirement'],
  /**
   * 视频页「分镜参考图」—— **镜头层**（每段「画什么」），不是整份提示词。
   *
   * ⚠️ 与 `prompt/refine` 的 `kind: 'storyboard'` 是两回事：那一条让模型**整份重写**
   * 分镜提示词（连「3×3 等分」「镜号 1-9」这些锁区一起过它的手，靠 system 里求它别改）；
   * 这一条只让模型写**每段的画面描述**，锁区由模板原样拼回。
   *
   * 值 `shots` 是**多行文本**（`_parse_fields` 把数组拼成换行分隔），调用方按行取；
   * `AiFillButton` 的通用 `patch()` 装不下它 —— 段数要对回 `videoStoryboard()`，
   * 所以这一档由页面自己 `split('\n')` 并校验段数（不符就整体不覆盖）。
   */
  'board-shots': ['shots'],
  /**
   * 商品库（`/product-assets` 的「新增 / 编辑商品」抽屉）—— 标题 + 卖点。
   *
   * ⚠️ **`title` 出现在白名单里，这是全站唯一一档**。其余七档的产品名都是
   * AI 的**输入**（`build*FillBrief` 里那个 `productName`），不是它的产出；
   * 而这一页的标题本来就短 —— 用户的原话是「有时候只有图片跟简单标题」，
   * 把四个字的短标题撑成能上架的标题正是他要省的活。
   *
   * ⚠️ **不含 `language` / `platform` / `sku`**：
   *   · 前两个是**选择器**（枚举 id），理由与 `feature` 那条完全相同 ——
   *     模型吐枚举 id，前端就失去了「能选什么」的权威；它们在本链路里是
   *     **输入**（决定写什么语言、按哪个平台的写法），由 `brief` 带过去；
   *   · `sku` 是用户自己的货号，AI 编一个出来比留空更糟。
   */
  product: ['title', 'sellingPoints'],
} as const

export type FillFormResponse = {
  /**
   * 键 = 字段名（`mainCopy` / `subCopy` / `sellingPoints` / `extra`），值 = 该字段的文案。
   *
   * **白名单裁决在后端**（`_FILL_FIELDS`）：模型多返回的键传不到这里，
   * 所以调用方不必再筛一遍，拿到就 patch。后端也会保证这个对象非空 ——
   * 「花了钱、提示成功、输入框一个字没变」是最坏的失败形态，它选择直接报错。
   */
  fields: Record<string, string>
  model: string
  cost: number
  /** 同 `RefinePromptResponse.coins`：本次扣掉的积分，由后端给，前端不许自己乘 */
  coins: number
  tokens: number
  elapsedMs: number
}

export type UploadResult = {
  /** 实际用于显示与提交的地址：优先图床公网 URL，图床不可用时为本站相对地址 */
  downloadUrl: string
  /** 本站相对地址（/static/uploads/...），同源可达，图床挂了也能看 */
  localUrl?: string
  /** 图床公网地址，未走图床时为 null */
  remoteUrl?: string | null
  /** 承载图片的主机名，如 i.ibb.co；本地兜底时为空 */
  host?: string
  fileName: string
  size: number
  mime: string
  width?: number
  height?: number
}

export type AssetItem = {
  id: string
  url: string
  /** 本地副本地址；图床图被删时的兜底 */
  localUrl?: string
  thumbUrl: string
  name: string
  kind: OutputKind
  size?: number
  width?: number
  height?: number
  createdAt: string
  source: 'upload' | 'generated'
  /** 图片承载方（i.bb.co = 图床，空 = 本地），UI 上标出来省得排查 */
  host?: string
  /** 资产类别；留空时后端按 context 推导，见 config/assetTaxonomy.ts */
  assetCategory?: string
  /** 上传时所在的工作流（app_slug 或页面 slug）—— 决定这张图的类别 */
  context?: string
}

export type WorkItem = {
  id: string
  title: string
  workType: OutputKind
  url: string
  thumbUrl: string
  contentText?: string
  sourcePage: string
  sourceModel: string
  prompt?: string
  favorite?: boolean
  createdAt: string
  /** 幂等键，后端去重用 */
  archiveKey: string
  /** 产出它的任务 id —— 「我的作品」按任务聚成图集的唯一依据 */
  jobId?: string
  /** 资产类别 id（model / product / scene / white-bg …） */
  assetCategory?: string
  appSlug?: string
  origin?: 'generated' | 'upload'
}

/* -------------------------------------------------------------------------- */
/* 资产库（作品 + 上传素材的统一视图）                                          */
/* -------------------------------------------------------------------------- */

export type AssetLibraryItem = {
  id: string
  title: string
  url: string
  thumbUrl: string
  /** 本地副本地址；图床挂了时 SmartImage 自动降级到它（作品没有这一项） */
  localUrl?: string
  kind: OutputKind
  assetCategory: string
  origin: 'generated' | 'upload'
  sourcePage: string
  sourceModel: string
  appSlug: string
  prompt?: string
  contentText?: string
  favorite?: boolean
  /**
   * 产出它的任务 id。
   * - 有值 → 同一任务的 N 张产出，在「我的作品」里聚成**一个任务图集**
   * - 空串/缺失 → 上传素材，或无法归组的存量作品，各自单张成卡
   *
   * 有了它，「我的作品」才不必靠标题或时间去猜哪几张属于同一次生成。
   */
  jobId?: string
  createdAt: string
}

export type AssetCategoryStat = {
  category: string
  label: string
  count: number
  generated: number
  upload: number
}

export type AssetLibraryStats = {
  total: number
  works: number
  assets: number
  /**
   * 收藏数（只在作品上有意义）。首页那张「作品总数」卡读的是这里 ——
   * 它以前数的是浏览器镜像（`hw_mirror_works_v1`），清站点数据就归零，
   * 而库里的作品明明还在。**计数一律以库为准**。
   */
  favorites: number
  categories: AssetCategoryStat[]
  origins: { id: string; label: string; desc: string }[]
}

export type AssetLibraryQuery = {
  category?: string
  origin?: 'all' | 'generated' | 'upload'
  kind?: 'all' | 'image' | 'video' | 'text'
  q?: string
  page?: number
  perPage?: number
}

/* -------------------------------------------------------------------------- */
/* 商品资产（product_assets）—— 「一个商品的档案」，详情页直接引用它             */
/* -------------------------------------------------------------------------- */
/*
 * 与 `AssetItem` / `AssetLibraryItem` 的分工（**两组都要有，不是重复**）：
 *   · 那两个是**一张图一条**（散图池），回答「我手上有哪些图」；
 *   · 这里是**一个商品一条**，回答「这个商品叫什么、卖点是什么、用哪几张图」。
 *
 * 详情页要的是后者：它一次要「标题 + 卖点 + 一组产品图」，而从散图池里挑图，
 * 挑完还得回去自己填标题与卖点 —— 那正是这套东西要省掉的活。
 *
 * 字段名与后端 `contracts.ProductAssetItem` 逐字对齐（都是 camelCase）。
 */

export type ProductAssetImage = {
  /** 主地址（图床或本站静态） */
  url: string
  /** 缩略图。没有就用 url */
  thumbUrl?: string
  /** 本地副本，图床被删/被墙时 SmartImage 的降级目标 */
  localUrl?: string
  /** 原始文件名。批量导入时它是「这张图是哪个文件」的唯一线索 */
  name?: string
}

export type ProductAsset = {
  id: string
  /** 归属账号。**由后端下发**，前端不许自己记一份（换账号那一刻就会错） */
  userId: string
  title: string
  sku: string
  /** 卖点，一行一个（与详情页的卖点输入同一种写法） */
  sellingPoints: string
  /** 该商品文案 / 图内文字的语言，如 `zh-CN` / `en-US` */
  language: string
  platform: string
  /**
   * 所属分类的**名字**（不是 id）。空串 = 未分类。
   *
   * ⚠️ 这里存的就是名字，没有 `categoryId` 那种东西 ——
   * 分类是一个字符串字段，「有哪些分类」由 `ProductCategory` 回答。
   * 理由见后端 `db.SCHEMA` 里 `product_categories` 那段。
   */
  category: string
  /** 图组。**第一张 = 主图**（顺序有语义，不许重排） */
  images: ProductAssetImage[]
  /** 封面 = `images[0]` 的派生冗余（后端算，写入侧不接受指定） */
  coverUrl: string
  coverLocal: string
  /** manual = 表单新增；import = 批量导入 */
  source: 'manual' | 'import'
  note: string
  createdAt: string
  updatedAt: string
}

export type ProductAssetQuery = {
  /** 标题 / SKU / 卖点三列模糊搜索 */
  q?: string
  language?: string
  platform?: string
  /**
   * 分类筛选。三种取值：
   *   · 不传 / `''` → 全部
   *   · `UNCATEGORIZED_CATEGORY` → 只看未分类
   *   · 其它 → 某个分类名
   */
  category?: string
  page?: number
  perPage?: number
}

/**
 * 「未分类」在筛选参数里的哨兵值。**必须与后端 `db.UNCATEGORIZED_CATEGORY` 逐字相同**。
 *
 * ⚠️ 为什么不能直接用空串表示未分类：空串在 query 里等于「这个参数没传」，
 * 于是「只看未分类」和「看全部」会变成同一个请求 —— 那颗 chip 点下去毫无反应，
 * 而它在代码里看着完全正常（`category: ''` 传出去了）。所以未分类需要一个非空代号。
 * `smoke-product-assets.ts` 里有一条断言比较它俩，改一处会让那条断言红。
 */
export const UNCATEGORIZED_CATEGORY = '__uncategorized__'

/** 一个商品分类。**没有商品数** —— 那个数在 facets 里，只算一份 */
export type ProductCategory = {
  id: string
  userId: string
  name: string
  /** 排序，小的在前。用户排的序不该被商品数量打乱 */
  sortOrder: number
  createdAt: string
  updatedAt: string
}

/** 分类筛选条上的一颗 chip：分类本身 + 它名下有几件货 */
export type ProductAssetFacetCategory = {
  value: string
  count: number
  id: string
  sortOrder: number
}

/** 筛选条上的一颗 chip：真实取值 + 拥有它的商品数 */
export type ProductAssetFacetValue = { value: string; count: number }

/**
 * 筛选条的真实取值分布（`GET /api/product-assets/facets`）。
 *
 * ⚠️ 与 `config/productAssets.ts` 的 `PRODUCT_LANGUAGES` **不是一份东西**：
 * 那份是「新增表单里能选哪些语言」的枚举；这份是「库里已经有哪些语言」的统计。
 * 拿枚举当筛选条会铺一排点了必空的 chip，拿统计当表单选项则选不到还没用过的语种。
 *
 * ⚠️ `categories` 又是第三种东西：它是**清单**而不是统计 ——
 * 用户新建一个空分类，它必须立刻出现在筛选条上（否则他以为没建成）。
 * 所以分类那一项带 `id` 与 `sortOrder`，且**可能 `count: 0`**。
 */
export type ProductAssetFacets = {
  total: number
  images: number
  /** 未分类（`category` 为空）的商品数。未分类不是一个分类，所以它是单独一个数 */
  uncategorized: number
  languages: ProductAssetFacetValue[]
  platforms: ProductAssetFacetValue[]
  categories: ProductAssetFacetCategory[]
}

export type CreateProductAssetRequest = {
  /** 必填。空标题的商品在列表里是一条认不出的横线（后端会 400） */
  title: string
  sku?: string
  sellingPoints?: string
  language?: string
  platform?: string
  /** 分类名。填一个**还不存在**的名字时后端会自动建这个分类 */
  category?: string
  images?: ProductAssetImage[]
  note?: string
}

/**
 * `PATCH` 的 body，全部可选。
 *
 * ⚠️ `images` 传了就是**整组替换**，不是追加 —— 界面上「删图 / 加图」改的是
 * 同一个本地数组，最后一起提交。
 */
export type UpdateProductAssetRequest = Partial<CreateProductAssetRequest>

export type BulkCreateProductAssetsResponse = {
  created: number
  items: ProductAsset[]
}

/** 分类改名 / 删除的回执。两个动作都会**动到商品**，所以要带受影响的商品数 */
export type RenameProductCategoryResponse = {
  category: ProductCategory
  /** 跟着改了分类的商品数（改名）或回落成未分类的商品数（删除） */
  affected: number
}

/* -------------------------------------------------------------------------- */
/* 任务历史（后端任务表）                                                      */
/* -------------------------------------------------------------------------- */

/**
 * 后端任务表里的一条任务（`GET /api/aigc/jobs`）。
 *
 * 与 `JobCenterContext.Job` 的分工：
 * - `Job` 是**浏览器侧的会话状态**，带本地推进的进度条、模型族 id，只活在当前浏览器；
 * - `ServerJobItem` 是**已落库的事实**，`error` / `usage` / `inputs` 都在里面。
 *
 * 为什么一定要读它：任务中心原来只读 localStorage（上限 60 条）。
 * 清缓存、换浏览器或超过 60 条之后，任务与**失败原因**一起消失 ——
 * 用户只知道「失败了」，却查不到为什么。库里一直存着，缺的只是读出来。
 */
export type ServerJobItem = {
  jobId: string
  appSlug: string
  model: string
  provider: string
  platform: string
  /**
   * 批次 id。同一批「批量表格」的 N 个 job 共用它，**空串 = 不是批量来的**。
   *
   * ⚠️ 判空串不能省：老库补列后的历史行全是空串，若把空串也当批次 id，
   * 所有**单个**提交的任务会被聚成一批（右栏出现一张「N 个产品」的假批次卡）。
   */
  batchId: string
  assetCategory: string
  sourcePage: string
  title: string
  prompt: string
  state: JobState
  outputs: JobOutput[]
  /** 失败原因：code 用于分类（该充值 / 该换 Key / 该重试），message 是给人看的 */
  error: JobError | null
  /** 原始输入与参考图 —— 「重试」要用；缺了它重试只能退化成拿标题瞎跑 */
  inputs: Record<string, unknown>
  imageUrls: string[]
  aspectRatio: string
  resolution: string
  expectedOutputs: number
  estimatedMs: number
  free: boolean
  usage: { consumeCoins?: number; thirdPartyConsumeMoney?: number } | null
  /**
   * 已被用户「清理」过（任务卡上的「移除记录」/ 任务中心的「清空已结束」；
   * 生成页结果卡那颗「清空」只收起视图，**不置这一位**）。
   *
   * **库里的行永远在**，这个位只表示「默认列表里不出现」。所以：
   * · `hidden: true` 的记录在「任务中心 → 已清理」里能恢复，也可以清缓存 / 换浏览器后照样找到；
   * · 它**不是** `deleted` —— 任务的账（消耗、失败原因、原参数）是用来复盘和重试的。
   */
  hidden?: boolean
  createdAt: string
  finishedAt: string | null
}

export type JobListQuery = {
  limit?: number
  /** all 不筛；active = 排队中 + 生成中 */
  state?: 'all' | 'active' | 'succeeded' | 'failed'
  /**
   * 可见性口径。默认 `exclude`（只看没清理过的）。
   * `only` = 只看已清理的（恢复用）；`all` = 全给（对账用）。
   */
  hidden?: 'exclude' | 'only' | 'all'
}

export type UsageRecord = {
  id: string
  createdAt: string
  appName: string
  sourcePage: string
  model: string
  status: 'success' | 'failed'
  coins: number
  cost: number
  outputs: number
}

export type UsageSummary = {
  totalCalls: number
  totalCoins: number
  totalCost: number
  recent24h: number
  successRate: number
  byDay: { day: string; calls: number; cost: number }[]
  byModel: { model: string; label: string; calls: number; cost: number }[]
  byPage: { page: string; label: string; calls: number }[]
  records: UsageRecord[]
}

export type Identity = {
  userId: string
  displayName: string
  avatar?: string
  /**
   * 剩余额度。
   *
   * ⚠️ **不是元**：1 元 = 10 额度（后端 `pricing.coins_of`）。一张图的报价
   * 折下来通常就是个位数到几十 —— 看到「余额 20000」不要当成两万块钱。
   * 类型是 number 而不是整数：1.5 额度是合法值，取整会让账目对不上。
   */
  coinBalance: number
  plan: string
  /** 登录账号（songshuai@hsy.com） */
  account: string
  /** 累计已用额度（来自额度账本，不是「初始 − 剩余」推出来的） */
  coinUsed: number
}

/**
 * 会员体系：**会员**账号 / 会话。
 *
 * ⚠️ 2026-10-07 拆表后**没有 `role`**：管理员住在另一张表（`admins`），
 * 走另一套登录（`/api/admin/auth/*`）。会员没有「是不是管理员」这个属性。
 * 想判「当前能不能进后台」，判据是**「后台那一侧登录了没有」**
 * （`useAdminAuth()`），不是这张表上的某个字段。
 */
export type AuthUser = {
  id: string
  account: string
  displayName: string
  coins: number
  plan: string
  status: 'active' | 'disabled'
  createdAt: string
  updatedAt: string
}

/**
 * **后台账号**（`admins` 表）。与 `AuthUser` 刻意不共用类型 —— 字段集不同
 * 才是这两张表分开的意义：后台身份**不持有额度**（连 `coins` 都没有）。
 */
export type AdminUser = {
  id: string
  account: string
  displayName: string
  status: 'active' | 'disabled'
  lastLoginAt: string
  createdAt: string
  updatedAt: string
}

export type LoginResponse = {
  token: string
  user: AuthUser
}

/** 后台登录响应。键名是 `admin` 而不是 `user` —— 两份响应形状不同。 */
export type AdminLoginResponse = {
  token: string
  admin: AdminUser
}

/**
 * 一个可切换的上游（后台管理里的一行）。
 *
 * ⚠️ **没有完整 Token 字段**：后端只下发脱敏串（`abcd••••••wxyz`）。
 * 想改就整串重填 —— 让「读回来再写回去」这条路走不通是刻意的，
 * 免得任何一次前端报错 / 截图把 Key 带出去。
 */
export type UpstreamItem = {
  id: string
  label: string
  note: string
  /** 该行要渲染哪几个输入框（baseUrl / apiKey / model） */
  fields: ('baseUrl' | 'apiKey' | 'model')[]
  modelHint: string
  baseUrl: string
  model: string
  apiKeyMasked: string
  hasApiKey: boolean
  /** 脚本里有内置 Key：Token 留空照样能用，别当成「没配」 */
  builtinKey: boolean
  /** 被后台改动过的字段。空数组 = 全用默认值 */
  overridden: string[]

  /* ---- 2026-10-07 通道化（后端追加下发；老字段一个都没动） ---- */

  /** 它属于哪个适配器家族。家族是**代码**，配不出来 —— 新增时只能从 `families` 里选。 */
  family: string
  /** 通道标签（= 家族 id）。用于「余额不足该去哪充值」这类归属提示 */
  channel: string
  kinds: string[]
  /** 代码里的默认行。**不能被删掉「最后一条」**；清单本身可用「恢复默认」还原 */
  builtin: boolean
  /**
   * 该家族**当前启用的就是它**。
   *
   * 🔴 同一家族同时只有一条 `active` —— 这不是产品选择，是物理约束：
   * skill 模块在进程里只有一份，Key 落在模块属性/环境变量上，两条并存必然互相覆盖。
   * 所以「新增通道」= 备一份，「切过去」才生效。UI 必须把这件事说出来，
   * 否则管理员会以为加了两条就有了两条链路。
   */
  active: boolean
  /** 家族还在代码里。false = 这条通道没有适配器接得住（删过家族/手改过库） */
  available: boolean
  /** 报价覆盖。null = 沿用 `pricing.PROVIDER_PRICE` 的默认口径 */
  unitPrice: number | null
  unit: string
}

export type UpstreamGroup = {
  kind: 'text' | 'image' | 'video'
  label: string
  sub: string
  items: UpstreamItem[]
}

/** 适配器家族（代码常量）。「新增通道」时从这里选，**不是自由文本**。 */
export type UpstreamFamily = {
  family: string
  label: string
  kinds: string[]
  note: string
  fields: ('baseUrl' | 'apiKey' | 'model')[]
  modelHint: string
  /** 该家族默认的计价单位，给新增表单当占位提示 */
  unit: string
  builtinKey: boolean
}

export type UpstreamCatalog = { groups: UpstreamGroup[]; families: UpstreamFamily[] }

export type UpstreamUpdate = {
  baseUrl?: string
  apiKey?: string
  model?: string
}

/**
 * 改动接口（新增 / 删除 / 切启用 / 恢复默认）的返回：**整份新清单**。
 *
 * 为什么每次回整份而不是「改了哪几个字段」：启用态是同族**互斥**的，改一条会
 * 影响另一条的显示。只回增量的话前端得再补一次 GET，而那两次请求之间状态已变，
 * 界面会出现「显示的和实际的不一致」—— 一次读一次写、语义一致，才能免掉这类错位。
 */
export type UpstreamCatalogResult = UpstreamCatalog & {
  created?: string
  id?: string
  removed?: boolean
  family?: string
  active?: string | null
}

export type UpstreamCreate = {
  family: string
  label: string
  id?: string
  /** 报价覆盖：**要么单价与单位一起给，要么都不给**（后端会 400） */
  unitPrice?: number
  unit?: string
}

export type AdminOverview = {
  counts: Record<string, number>
  users: number
  activeUsers: number
  totalCoins: number
  spentCoins: number
  /**
   * 后台账号数。**单列，不并进 `users`** —— 两张表是两个概念，
   * 加起来得到的那个数字没有任何人能用它回答一个问题。
   */
  admins: number
}

export type CoinLedgerItem = {
  id: string
  createdAt: string
  kind: 'grant' | 'spend' | 'refund' | 'adjust'
  coins: number
  balanceAfter: number
  ref: string
  note: string
}

export type CreateUserRequest = {
  account: string
  password: string
  displayName?: string
  coins: number
  plan?: string
}

export type UpdateUserRequest = {
  displayName?: string
  password?: string
  /** **设为某个值**（不是增量）——管理员在界面上填的就是目标额度 */
  coins?: number
  plan?: string
  status?: 'active' | 'disabled'
}

/** 新建**后台账号**。没有 coins —— 后台身份不参与计费。 */
export type CreateAdminRequest = {
  account: string
  password: string
  displayName?: string
}

export type UpdateAdminRequest = {
  displayName?: string
  password?: string
  status?: 'active' | 'disabled'
}

/* -------------------------------------------------------------------------- */
/* 用户自存的视频模板                                                          */
/* -------------------------------------------------------------------------- */

/**
 * 用户在视频工作台**自己存下来**的一条模板（`GET /api/video-templates`）。
 *
 * 与 `config/videoTemplates.ts` 内置那 8 条的差别**只有一处，但很关键**：
 * 内置是随代码发布的静态清单（id 形如 `vt-01`），这里是**某个账号的私有数据**
 * （id 形如 `vmt_ab12cd`），本人可增删改、别人看不见。前端把两者归一成同一个
 * 形状再渲染（见 `mergeVideoTemplates`），所以字段名必须与内置模板对齐 ——
 * 少一个 `storyboard`，套用时就只有参数被铺开、分镜还是上一条的。
 */
export type CustomVideoTemplate = {
  id: string
  /** 归属账号。模板中心靠它判断「这条是不是我的」（决定要不要渲染删除键） */
  userId: string
  name: string
  /** 卡面角标（内置模板是「卡点换装」这类话题） */
  tag: string
  description: string
  /** 归属档位，与内置模板同一套值（当前只有 `short-video`） */
  modes: string[]
  /** 五个参数。**必须五个都在** —— 少一个 `activeTemplate()` 就永远点不亮 */
  preset: {
    scene: string
    style: string
    camera: string
    duration: number
    aspectRatio: string
  }
  /**
   * 分镜脚本。形状真源在 `config/prompts.ts` 的 `Storyboard`
   * （多镜头 `{shots}` 或三段式 `{open,body,close}`）——
   * 这里不 import 它，避免 `api/types.ts` 反向依赖 `config/`（那一层本来就 import 本文件）。
   * 消费侧（`mergeVideoTemplates`）做一次 cast，是唯一一处需要知道形状的地方。
   */
  storyboard: Record<string, unknown>
  /** 跨全片结构约束（进提示词【补充】段） */
  shot: string
  /** 封面 = 用户生成的那张 3×3 分镜九宫格（**后端已转存**，不是上游直链） */
  coverUrl: string
  /** 封面本地副本（相对地址），图床降级时用 */
  coverLocal: string
  /** 这条模板真正产出的成片（模板卡播放键播它；空 = 回落逐格播分镜） */
  sampleUrl: string
  createdAt: string
  updatedAt: string
}

/** `POST /api/video-templates` 的 body。字段与库里那几列一一对应。 */
export type CreateVideoTemplateRequest = {
  name: string
  tag?: string
  description?: string
  modes?: string[]
  preset: CustomVideoTemplate['preset']
  storyboard: Record<string, unknown>
  shot?: string
  /**
   * 封面地址。**可以直接传上游直链** —— 后端在落库前会把它拉回来转存
   * （分镜九宫格在上游是带签名过期的，原样存进库第二天就是一排灰块）。
   */
  coverUrl?: string
  sampleUrl?: string
}

export type UpdateVideoTemplateRequest = {
  name?: string
  tag?: string
  description?: string
  /**
   * 模板内容 —— 分镜脚本。**整份替换**，不做逐镜头合并。
   *
   * 2026-10-08 用户口径「提示词模板应该是可以编辑的」：模板里跟提示词有关的
   * 文字只有两块（这块 + `shot`），不给它们开口子的话，用户在模板中心点开
   * 「编辑模板」只能改名字，想动一句画面描述就得删掉重存一条。
   */
  storyboard?: Record<string, unknown>
  /** 模板内容 —— 跨全片约束（进提示词【补充】段，单行） */
  shot?: string
  coverUrl?: string
}

/** 数据层统一接口：mock 与真实后端都实现它 */
export interface ApiClient {
  readonly mode: 'mock' | 'http'
  me(): Promise<Identity>
  /**
   * 后端存活预检（打 `GET /api/aigc/health`，走 20s 短超时）。
   *
   * 为什么需要它：上传是全站唯一一个「可能合法地跑几分钟」的请求，
   * 它的超时只能设得很长（见 client.ts 的 UPLOAD_TIMEOUT_MS）。
   * 没有预检的话，后端僵死时用户点了上传就得盯着转圈的框等满那几分钟，
   * 而真正的原因是「后端没响应」—— 一句话就能说清的事。
   * 所以：**长活开始前先花 3ms 确认后端活着，死了就立刻报错。**
   */
  ping(): Promise<void>
  upload(file: File): Promise<UploadResult>
  /**
   * 真实可选的模型清单（聚合后的「一个上游模型一条」，不是底层路由 slug 表）。
   * 后端不可用时降级到内置兜底清单，见 config/modelFamilies.ts。
   */
  /**
   * 模型清单。`kind` 用于按产出类型过滤。
   * `text` 是文案类（LLM）—— 与 image/video 不是同一条执行链路，
   * 后端对它的处理是同步调用而非「提交 + 轮询」。
   */
  listModels(kind?: 'image' | 'video' | 'audio' | 'text'): Promise<ModelFamilyInfo[]>
  /**
   * skill 加载器的可观测面：后端环境开关 + 磁盘上真实存在的技能清单。
   *
   * 存在的意义是**让「模型能读到的名字」可被核对** —— 实测模型会自造技能名
   * （把 ozon-image-prompt-generator 写成 ozon-copywriting），排查时得能一眼
   * 看出清单里到底有没有那个名字。
   */
  listSkills(): Promise<SkillIndex>
  /**
   * 数字人口播的**音色清单**（`GET /api/aigc/voices`）。
   *
   * 真源是后端那侧的 `apilio-tts` skill（88 个实测音色，按语种 / 用途分组）。
   * 前端**不许再维护一份可选音色清单** —— 硬编码的下场是 skill 删了一条、
   * 页面照样把它发出去，表现为「选了 A 出来 B」而所有状态全绿。
   *
   * 拉不到时由 `config/voices.ts` 的兜底清单顶上（5 条，都被跨仓库测试钉住）。
   */
  listVoices(): Promise<VoiceCatalog>
  /**
   * 试听一个音色（数字人「口播与音色」页签那颗试听按钮）。
   *
   * ⚠️ **它会花钱**（首次）：后端按「引擎 + 音色 + 试听文本」缓存，
   * 第一次真的调 TTS 合成并落一条 usage，之后同一个音色**命中缓存不花钱**。
   * 所以这个调用**不该被放进循环或 effect 里自动触发** —— 只有用户点了才发。
   *
   * 试听文本是**服务端常量**（「你好，我是这个音色……」），前端传不了：
   * 能传的话这条端点就是一个能合成任意内容的免费 TTS。
   *
   * 返回的 `url` 直接给 `<audio src>`；`cached` / `billed` 后端都给了，
   * 前端不自己推（推导会漂，漂了就是「界面说没花钱、账本上有一笔」）。
   */
  previewVoice(req: VoicePreviewRequest): Promise<VoicePreviewResponse>
  pricePreview(req: PricePreviewRequest): Promise<PricePreviewResponse>
  /**
   * 让后端把一份提示词交给 LLM 再写一版（分镜提示词区的「AI 写一版」按钮）。
   *
   * 与 `run()` 的区别是**根本性的**，别把它当生成任务用：
   * - 不建 job、不进任务中心、没有 jobId 可以轮询，**同步返回一段文本**；
   * - 但它真花钱（一次 ¥0.10，后端落 usage），所以不是可以随便重试的读接口。
   *
   * 上下文由调用方组装成 `brief`（提示词模板的真源在前端 `config/prompts.ts`，
   * 后端不重复拼一份 —— 见该端点的实现注释）。
   */
  refinePrompt(req: RefinePromptRequest): Promise<RefinePromptResponse>
  /**
   * 让后端按上下文把几个表单字段一次补齐（`/banner` 的「AI 填充」按钮）。
   *
   * 与 `refinePrompt` 同族：不建 job、同步返回、按次 ¥0.10 落 usage。
   * 区别只在**回来的东西**：改写回一段正文，填充回**结构化字段**
   * （键名与表单字段同名，拿到就能 patch）。
   *
   * 同理**不能重试**：它是按次计费的写动作，重打一次就是白扣一毛。
   */
  fillForm(req: FillFormRequest): Promise<FillFormResponse>
  /**
   * 分析一条参考视频，拆出镜头结构（视频复刻第 1 页的「分析视频」按钮）。
   *
   * 与前两条同族：不建 job、不进任务中心、同步返回。区别在两处：
   *   ① 上游读的是**整条视频**（多模态），所以单价另算一档（¥0.30/次）；
   *   ② 回来的是一份**镜头清单**（结构化），不是一段正文。
   *
   * 同理**不能重试**：它是按次计费的写动作，重打一次就是白扣三毛。
   */
  analyzeVideo(req: AnalyzeVideoRequest): Promise<AnalyzeVideoResponse>
  run(req: GenerateRequest): Promise<RunResponse>
  poll(appSlug: string, jobId: string): Promise<PollResponse>
  /**
   * 任务历史（后端库里的任务，含**失败原因**）。
   * 任务中心 / 我的作品的数据源之一 —— 与本地会话任务合并后展示。
   *
   * 返回 `{ items, hiddenCount }`：`hiddenCount` 是**库里**已清理的总条数，
   * 与本次 `limit` 无关。让前端自己数一页会越数越少（截断之后就不准了），
   * 而「已清理 N 条」这个数字是要点进去恢复的入口，报小了就等于藏了记录。
   */
  listJobs(params?: JobListQuery): Promise<{ items: ServerJobItem[]; hiddenCount: number }>
  /** 账号口令换 token。失败一律是同一句「账号或密码不正确」（后端刻意不区分） */
  login(account: string, password: string): Promise<LoginResponse>
  /** 退出：服务端作废 token（本地也清） */
  logout(): Promise<void>
  /**
   * 校验当前会话是否还有效，并把最新余额带回来。
   * 进站时打一次 —— token 过期 / 账号被停用都会在这里变 401。
   */
  session(): Promise<AuthUser>
  /** 本账号的额度账本（每一笔进出账） */
  myLedger(limit?: number): Promise<CoinLedgerItem[]>
  /**
   * —— 后台侧：**另一套登录，另一个 token** ——
   *
   * 会员与后台是两个身份（后端两张表），前端也存两个 token：
   * 这一组方法走的是 `/api/admin/auth/*`，把 token 存在**另一个键**里。
   * 于是「同时开着工作台和后台」是合法状态，退出后台也不会把工作台踢下线。
   *
   * ⚠️ 别把它们和上面的 `login/logout/session` 混起来用 ——
   * 两边的账号在对方那里都是「不存在」（后端刻意如此）。
   */
  adminLogin(account: string, password: string): Promise<AdminLoginResponse>
  adminLogout(): Promise<void>
  adminSession(): Promise<AdminUser>
  /** —— 以下仅后台账号可用（后端 require_admin 拦） —— */
  listUpstreams(): Promise<UpstreamCatalog>
  updateUpstream(
    providerId: string,
    patch: UpstreamUpdate,
  ): Promise<{ id: string; changed: string[] }>
  /**
   * 新增一条通道（挂在已有适配器家族下）。
   *
   * 这三个改动接口都返回**整份新清单** —— 启用态是同族互斥的，改一条会影响另一条
   * 的显示，分两次读会出现错位。所以调用方直接用返回值刷界面，不要再补一次 GET。
   */
  createUpstream(req: UpstreamCreate): Promise<UpstreamCatalogResult>
  deleteUpstream(providerId: string): Promise<UpstreamCatalogResult>
  activateUpstream(family: string, channel: string): Promise<UpstreamCatalogResult>
  resetUpstreamChannels(): Promise<UpstreamCatalogResult>
  adminOverview(): Promise<AdminOverview>
  listUsers(): Promise<AuthUser[]>
  createUser(req: CreateUserRequest): Promise<AuthUser>
  updateUser(userId: string, patch: UpdateUserRequest): Promise<AuthUser>
  /** 后台账号列表。**与 `listUsers()` 是两个端点**（两张表）。 */
  listAdmins(): Promise<AdminUser[]>
  createAdmin(req: CreateAdminRequest): Promise<AdminUser>
  updateAdmin(adminId: string, patch: UpdateAdminRequest): Promise<AdminUser>
  deleteAdmin(adminId: string): Promise<{ ok: boolean }>
  /**
   * 删账号。**只删账号本身**：它的任务、作品与账本留在库里（账是审计凭据，
   * 删账号不等于抹掉历史，否则「谁花的钱」就没答案了）。
   */
  deleteUser(userId: string): Promise<{ ok: boolean }>
  userLedger(userId: string): Promise<CoinLedgerItem[]>
  /**
   * 「清理」一批任务记录（任务卡上的「移除记录」/ 任务中心的「清空已结束」）。
   *
   * ⚠️ 它**不是删除**：后端只把 `hidden` 置 1，行原样留在库里。
   * 这份「哪些被清过」的状态必须落库而不是存浏览器 —— 上一版存在
   * localStorage（`hw_job_hidden_v1`），清缓存后视图状态没了，
   * 被移除的记录又全冒出来，用户看到的是「一页空白 + 一会儿自己变回来」。
   *
   * 未知 / 本地未落库的 id 由后端静默忽略（返回真正改动的条数）。
   */
  hideJobs(jobIds: string[]): Promise<number>
  /** 恢复被清理的记录（「任务中心 → 已清理」的恢复按钮） */
  unhideJobs(jobIds: string[]): Promise<number>
  /**
   * 通知后端停止跟进该任务。
   * 前端本地标记只是「不再显示」，后端不通知的话调度器会一直查到超时，白烧上游配额。
   */
  cancel(appSlug: string, jobId: string): Promise<void>
  listWorks(params?: {
    page?: number
    perPage?: number
    workType?: OutputKind
    category?: string
    origin?: string
  }): Promise<{
    items: WorkItem[]
    total: number
  }>
  deleteWork(id: string): Promise<void>
  toggleFavorite(id: string, favorite: boolean): Promise<void>
  listAssets(): Promise<AssetItem[]>
  addAsset(item: AssetItem): Promise<void>
  removeAsset(id: string): Promise<void>
  /**
   * 资产库统一视图：作品 + 上传素材合并，按类别 / 来源 / 类型筛选。
   * 旧的两个接口（listWorks / listAssets）保留，供参考图选择器等场景使用。
   */
  listAssetLibrary(params?: AssetLibraryQuery): Promise<{
    items: AssetLibraryItem[]
    total: number
    page: number
    perPage: number
  }>
  assetLibraryStats(): Promise<AssetLibraryStats>
  /** 手改类别 —— 自动推导兜底用（id 带 work_ / asset_ 前缀，后端据此分表） */
  updateAssetItem(id: string, patch: { assetCategory?: string; favorite?: boolean }): Promise<void>
  deleteAssetItem(id: string): Promise<void>
  /**
   * —— 用户自存的视频模板（2026-10-08）——
   *
   * 用户口径「用户改完模板可以存储起来」「抽结构出来存储到模板中心，封面是分镜图」。
   * 四个方法都是**零成本的元数据读写**：不建任务、不记账、不上游
   * （唯一的网络下行是后端在建模板时把分镜封面拉回来转存，那也不是上游生成）。
   *
   * ⚠️ 它们与 `config/videoTemplates.ts` 的 `VIDEO_TEMPLATES` **不是一份东西**：
   * 那边是随代码发布的内置清单，这里是某个账号自己存的。两者的合并出口只有一个
   * （`mergeVideoTemplates`），页面不许各拼一份。
   */
  listVideoTemplates(): Promise<CustomVideoTemplate[]>
  createVideoTemplate(req: CreateVideoTemplateRequest): Promise<CustomVideoTemplate>
  updateVideoTemplate(templateId: string, patch: UpdateVideoTemplateRequest): Promise<CustomVideoTemplate>
  deleteVideoTemplate(templateId: string): Promise<void>

  /*
   * 商品资产 —— 「一个商品一条档案」，供「主图 · 详情」页直接引用
   * （选中一个商品 → 自动带入产品名 + 卖点 + 产品图）。
   *
   * 与上面那组一样全是零成本元数据读写：不建任务、不记账、不上游。
   * 图片本身仍走 `upload()`（同一个上传通道），这里只存地址。
   */
  listProductAssets(params?: ProductAssetQuery): Promise<{ items: ProductAsset[]; total: number }>
  /** 筛选条的**真实取值分布**（从已存记录统计，不是写死的枚举） */
  productAssetFacets(): Promise<ProductAssetFacets>
  /** 按 id 取最新一条 —— 详情页选中后要拿库里的版本，不用列表里那份可能过期的镜像 */
  getProductAsset(assetId: string): Promise<ProductAsset>
  createProductAsset(req: CreateProductAssetRequest): Promise<ProductAsset>
  /**
   * 批量导入（一张 CSV 的 N 行一次提交）。
   * **整批校验、整批落库**：任何一行标题为空就整批 400 并点名第几行，
   * 不会「导入成功一半」。
   */
  bulkCreateProductAssets(req: {
    items: CreateProductAssetRequest[]
  }): Promise<BulkCreateProductAssetsResponse>
  updateProductAsset(assetId: string, patch: UpdateProductAssetRequest): Promise<ProductAsset>
  deleteProductAsset(assetId: string): Promise<void>

  /*
   * 商品分类 —— 「我的货怎么归类」，客户可以自己增 / 改 / 删。
   *
   * ⚠️ 分类名就是 `ProductAsset.category` 的值，所以这四个方法里
   * **只有 `list` 是读分类本身**，另外三个都会（或可能）动到商品：
   * 改名连带改商品，删除把商品回落成未分类 —— 两者的回执都带 `affected`，
   * 界面上必须把它说出来（用户唯一能判断「我有没有改错」的凭据）。
   */
  listProductCategories(): Promise<{ items: ProductCategory[] }>
  createProductCategory(req: { name: string; sortOrder?: number }): Promise<ProductCategory>
  updateProductCategory(
    catId: string,
    patch: { name?: string; sortOrder?: number },
  ): Promise<RenameProductCategoryResponse>
  deleteProductCategory(catId: string): Promise<RenameProductCategoryResponse>

  usage(): Promise<UsageSummary>
}

/* -------------------------------------------------------------------------- */
/* 模型族                                                                      */
/* -------------------------------------------------------------------------- */

// 'llm' 是文案（DeepSeek 官方）走的第三条通道：它既不进 duomi 也不进 songshuai 网关，
// 而是后端 app/llm.py 直连 /v1/chat/completions。后端 registry._channel_of() 已经下发
// 这个词，前端联合类型漏掉它就会在 modelFamilies.ts 里编译不过。
// 'youkou' 是第四条：贰春开放 API（`youkou-*` 的 14 个视频档），同样由
// registry._channel_of() 下发 —— 这里少写一个词，录 modelFamilies 时就编译不过。
// 'wuyin' 是第五条：速创 API（wuyinkeji）。2026-10-01 接入，用于 Google Omni ——
// 网关那条 google-omni 从接入起从没成功跑过（余额 ¥0.00），速创是唯一能出片的。
// 'apilio' 是第六条、'minimax' 是第七条：**两家都在做配音，但是两家不同的公司**。
// apilio 是网关（`apilio-tts`，数字人成片的配音 + `/tts` 走网关那两个引擎时），
// minimax 是 2026-10-08 起 `/tts` 默认引擎的落地处（`minimax-tts`，官方 Key、
// 官方余额）。合成实际发生在哪家，`channelLabel` 就得写哪家 ——
// 否则用量明细上的来源是假话，「余额不足该去哪充值」会指错地方。
// 'runninghub' 是第八条（数字人），同样由 `registry._channel_of()` 下发。
export type ModelChannel = 'duomi' | 'gateway' | 'llm' | 'youkou' | 'wuyin' | 'apilio' | 'minimax' | 'runninghub'

/**
 * 一个真实可选的模型。
 *
 * 存在的意义：底层路由表有 35 个 slug，其中 20 个是网关没有的上游、
 * 6 个是同一个模型的别名（「官方 / 图生图 / 渠道 / 渠道编辑」）。
 * 直接把它们渲染成下拉选项就是一屏假选项。这里按上游模型聚合。
 */
/** `GET /api/aigc/skills` 的单条技能（后端从 SKILL.md 的 frontmatter 现扫）。 */
export type SkillDocEntry = {
  name: string
  description: string
}

/** skill 加载器的可观测面。`enabled` = 后端环境开关（`HW_SKILLS_ENABLE`）。 */
export type SkillIndex = {
  enabled: boolean
  count: number
  /** 注入系统提示词的清单字符数（越大越贵，是开关成本的主要来源） */
  indexChars: number
  skillsDir: string
  skills: SkillDocEntry[]
}

/**
 * 一个可用音色。`id` 是**直接发给上游**的那个字符串（TTS 请求的 `voice` 字段），
 * `name` 只是我们给它的中文展示名。
 *
 * ⚠️ 别把两者调过来用：上游只认 `id`，把 `name`（「温暖女孩」）发过去会报
 * `2054 voice id not exist`，或者更糟 —— 回落到默认音色而任务照样成功。
 */
export type VoiceInfo = {
  id: string
  name: string
}

/** 音色分组。后端下发什么名字就用什么，前端不重排 —— 它是 skill 里那份表的顺序。 */
export type VoiceGroup = {
  label: string
  voices: VoiceInfo[]
}

/**
 * 音色清单接口的返回。真源是 `apilio-tts` skill 的 `SYSTEM_VOICES`。
 *
 * `count` / `labelledCount` 一起下发，是为了让「有没有漏配中文名」在接口上
 * 一眼可见（漏了会退回显示 ID 本身，页面不报错）。
 */
export type VoiceCatalog = {
  /**
   * 当前生效的配音引擎（`minimax` | `openai` | `minimax-direct`）。
   *
   * 为什么必须下发引擎：**配音模型与音色清单是同一个开关的两面** ——
   * 2026-10-06 MiniMax speech 渠道在 apilio 上整体 500，临时切到 `gpt-4o-mini-tts`，
   * 而它用的不是 MiniMax 那套音色 ID；2026-10-08 改为直连官方 `minimax-direct`，
   * 音色又换回 303 条官方那一套。界面若不知道自己拿的是哪一套，
   * 就会出现「下拉列着 MiniMax 音色、实际把稿子发给 OpenAI 模型」——
   * 上游不认那些 ID，症状是**选了 A 出来默认音色**，而任务状态全绿。
   */
  engine: string
  /** 引擎的展示名（如「MiniMax 官方直连」），照原样显示给用户 */
  engineLabel: string
  /** 界面默认音色 —— 一定是**当前引擎**里真实存在的上游音色 ID */
  default: string
  /** 合成用的模型（当前引擎的，如 `speech-2.8-hd` / `gpt-4o-mini-tts`），展示与排查用 */
  model: string
  /**
   * 真正执行合成的 skill 名（`minimax-tts` / `apilio-tts`）。
   *
   * 它**跟着引擎走**，不是常量：三个引擎里有两条走 apilio、一条走 minimax-tts。
   * 界面拿它做排查（「这一档到底由谁合成」），所以写死常量等于让它永远报错的答案。
   */
  skill: string
  count: number
  labelledCount: number
  groups: VoiceGroup[]
}

/**
 * 音色试听（`POST /api/aigc/voices/preview`）。
 *
 * 为什么值得一个专门的端点：下拉里只有一串中文名（「低沉男声」「温柔女声」），
 * 名字写得再准，也回答不了「这个声音适不适合我这条片子」—— 只能听一遍。
 * 没有试听，用户唯一的依据就是名字，而选错了要等整条数字人片子跑完
 * （几分钟 + 一次真实计费）才发现。
 *
 * ⚠️ 它是 `POST` 不是 `GET`：首次调用**真的会合成一次音频并计费**，
 * 不是一个没有副作用的读接口。
 */
export type VoicePreviewRequest = {
  /** 要试听的音色 ID（**上游 ID**，不是界面上的中文名）。必须是当前引擎清单里的那个 */
  voice: string
  /** 出处，只进用量账本 */
  sourcePage?: string
}

export type VoicePreviewResponse = {
  /**
   * 试听音频地址。**相对路径**（`/static/...`）—— dev server 把 `/static`
   * 一起代理到后端，绝对地址反而会在预览器里跨端口加载不出来。
   */
  url: string
  voice: string
  /**
   * 这次命中了服务端已有文件 —— **没花钱**。
   *
   * ⚠️ `cached` 与 `billed` 都由后端给，前端**不要自己推** `billed = !cached`：
   * 推导一旦和接口漂了，界面就会把「花了钱」说成「没花钱」（或反过来）。
   */
  cached: boolean
  /** 这次真的计费了 */
  billed: boolean
  engine: string
  model: string
  /** 本次费用（元）。命中缓存是 0 */
  cost: number
  /**
   * 本次**从额度里扣掉的积分**（1 元 = 10 积分）。命中缓存是 0。
   *
   * 与 `billed` 配套：`billed` 说「花没花」，`coins` 说「花了多少额度」——
   * 前端据此刷新顶栏。**不要拿 `cost × 10` 自己算**，那是第二份换算真源。
   */
  coins: number
  /** 合成耗时（毫秒）。命中缓存是 0 —— 它没等上游 */
  elapsedMs: number
}

export type ModelFamilyInfo = {
  id: string
  name: string
  // ⚠️ 这个联合类型必须与后端 registry.APP_KIND 的取值一一对齐。
  // 旧版写的是 'llm'，但后端从未下发过这个值（后端用 'text'）——
  // 两套叫法同时存在的结果就是：文案页永远筛不出任何模型，且没有任何报错。
  kind: 'image' | 'video' | 'audio' | 'text'
  available: boolean
  /** 走哪条通道：duomi 直连 / songshuai 网关 */
  channel: ModelChannel | null
  channelLabel: string | null
  unitPrice: number
  unit: string
  /** 实际提交用的路由 slug（无参考图） */
  textSlug: string | null
  /** 实际提交用的路由 slug（有参考图）；与 textSlug 相同表示上游不区分 */
  imageSlug: string | null
  /** 文生图与图生图共用同一 slug，UI 不必区分 */
  img2imgSameAsText: boolean
  note?: string | null
  /** 已在本项目真实出过图（区别于「网关里有这个 provider」） */
  verified: boolean
  reason?: string | null
  suggest?: string | null
  /**
   * 该模型的能力边界 —— **分辨率 / 比例 / 参考素材控件的唯一依据**。
   *
   * 来自后端 registry 的模型表（值抄自上游 catalog 实测，不是文档）。
   * 两条使用铁律：
   *   1. **空对象 `{}` 表示「后端没说」**（旧的网关模型就是这样），
   *      此时回落到全局默认清单；有值时一律以它为准 ——
   *      前端绝不许再写一份「哪些模型支持 1080P」，那份必然过期。
   *   2. `ref_video_max: 0` 是**明确的不支持**，不是「未知」。
   *      据此把参考视频上传框从界面上摘掉，别让用户传完才被上游拒。
   */
  capabilities?: ModelCapabilities
}

/** 模型能力边界。字段名与后端 `ModelRoute.options` 中的键逐字一致。 */
export type ModelCapabilities = {
  /** 可选清晰度。⚠️ 保留上游原样大小写（`720P` 与 `720p` 在不同模型下都出现过） */
  resolutions?: string[]
  /** 可选画面比例。`adaptive` 是「跟着参考素材走」 */
  aspect_ratios?: string[]
  /** 参考图上限；0 或缺失表示不支持 */
  ref_image_max?: number
  /** 参考视频上限；0 或缺失表示不支持 */
  ref_video_max?: number
}
